import {
  type DynamicClient,
  InvalidParamError,
} from '@dynamic-labs-sdk/client';
import type {
  ExecuteCheckoutTransactionParams,
  ExecuteCheckoutTransactionResult,
} from '@dynamic-labs-sdk/client/core';

import type { EvmSigningPayload } from '../../checkoutTransaction.types';
import { createWalletClientForWalletAccount } from '../../viem/createWalletClientForWalletAccount';
import { approveErc20 } from '../approveErc20';
import { getErc20Allowance } from '../getErc20Allowance';
import { isEvmWalletAccount } from '../isEvmWalletAccount';

/**
 * Executes an EVM checkout transaction by handling optional ERC-20 approval
 * and sending the main swap transaction on-chain.
 *
 * 1. Validates that the wallet account is an EVM wallet account
 * 2. If the signing payload includes an `evmApproval`, checks the current
 *    ERC-20 allowance and sends an approve transaction when insufficient
 * 3. Sends the main transaction using the viem wallet client
 *
 * @param params.signingPayload - The chain-aware signing payload containing `evmTransaction` and optional `evmApproval`.
 * @param params.walletAccount - The wallet account used to sign and send transactions.
 * @param params.onStepChange - Optional callback invoked with `'approval'` or `'transaction'` as each step begins.
 * @param client - The Dynamic client instance.
 * @returns The main transaction hash and, if an approval was sent, the approval transaction hash.
 */
export const evmExecuteCheckoutTransaction = async (
  {
    onStepChange,
    signingPayload,
    walletAccount,
  }: ExecuteCheckoutTransactionParams,
  client: DynamicClient
): Promise<ExecuteCheckoutTransactionResult> => {
  if (!isEvmWalletAccount(walletAccount)) {
    throw new InvalidParamError('Wallet account is not an EVM wallet account');
  }

  const evmPayload = signingPayload as EvmSigningPayload;

  if (!evmPayload.evmTransaction) {
    throw new InvalidParamError(
      'Signing payload is missing EVM transaction data'
    );
  }

  const { evmTransaction, evmApproval } = evmPayload;
  let approvalTransactionHash: string | undefined;

  if (evmApproval) {
    onStepChange?.('approval');

    const currentAllowance = await getErc20Allowance(
      {
        ownerAddress: walletAccount.address,
        spenderAddress: evmApproval.spenderAddress,
        tokenAddress: evmApproval.tokenAddress,
        walletAccount,
      },
      client
    );

    if (currentAllowance < BigInt(evmApproval.amount)) {
      const result = await approveErc20(
        {
          amount: BigInt(evmApproval.amount),
          spenderAddress: evmApproval.spenderAddress,
          tokenAddress: evmApproval.tokenAddress,
          walletAccount,
        },
        client
      );
      approvalTransactionHash = result.transactionHash;
    }
  }

  onStepChange?.('transaction');

  const walletClient = await createWalletClientForWalletAccount(
    { walletAccount },
    client
  );

  const transactionHash = await walletClient.sendTransaction({
    data: evmTransaction.data as `0x${string}`,
    to: evmTransaction.to as `0x${string}`,
    value: evmTransaction.value ? BigInt(evmTransaction.value) : 0n,
    ...(evmTransaction.gasLimit && { gas: BigInt(evmTransaction.gasLimit) }),
  });

  return { approvalTransactionHash, transactionHash };
};
